import { classnames } from "@app/utils/index.js";
import { defaultCountry, isValidForNotVatRegistration } from "@features/registration/events";
import useConfiguration from "@store/hooks/useConfiguration";
import React, { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { CheckoutFormSchema } from "../schema";
import type { ICheckoutAddress, ICheckoutCustomer } from "../types";
import ShippingAddressFieldset from "./ShippingAddressFieldset";

type IProps = {
  shippingAddresses?: ICheckoutAddress[];
  countries: Record<string, string>;
} & ICheckoutCustomer;

export default function StepCustomer({ shippingAddresses, countries }: IProps) {
  const { isAuthenticated } = useConfiguration();

  const { t } = useTranslation();
  const { control, register, formState, setValue } = useFormContext<CheckoutFormSchema>();
  const isCompany = useWatch({ control, name: "customer.isCompany" });
  const countryCode = useWatch({ control, name: "customer.billingCountryCode" });

  const showCompanyFieldset = useMemo(
    () => !(isAuthenticated && !isCompany),
    [isCompany, isAuthenticated],
  );

  const vatNumber = useWatch({ control, name: "customer.vatNumber" });
  const notVatRegistered = useWatch({ control, name: "customer.notVatRegistered" });
  const allowNotVatRegistered = useMemo(
    () => isCompany && isValidForNotVatRegistration(countryCode, vatNumber),
    [countryCode, vatNumber, isCompany],
  );

  return (
    <div className="customer w-full flex justify-center">
      <div className="max-h-full w-full grid grid-cols-1 gap-y-4">
        <fieldset className="form-section">
          <legend>{t("PersonalInformation")}</legend>

          <div className="form-fields">
            <div className="form-field">
              <div className="input">
                <input
                  id="customer.titlePrefix"
                  type="text"
                  autoComplete="honorific-prefix"
                  className="w-full as-text"
                  placeholder={t("TitlePrefix")}
                  {...register("customer.titlePrefix")}
                />
                {formState.errors?.customer?.titlePrefix?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.titlePrefix.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <div className="input">
                <input
                  id="customer.firstName"
                  type="text"
                  autoComplete="given-name"
                  className="w-full as-text"
                  placeholder={t("FirstName")}
                  {...register("customer.firstName")}
                />
                {formState.errors?.customer?.firstName?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.firstName.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <div className="input">
                <input
                  id="customer.lastName"
                  type="text"
                  autoComplete="family-name"
                  className="w-full as-text"
                  placeholder={t("LastName")}
                  {...register("customer.lastName")}
                />
                {formState.errors?.customer?.lastName?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.lastName.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <div className="input">
                <input
                  id="customer.titleSuffix"
                  type="text"
                  autoComplete="honorific-suffix"
                  className="w-full as-text"
                  placeholder={t("TitleSuffix")}
                  {...register("customer.titleSuffix")}
                />
                {formState.errors?.customer?.titleSuffix?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.titleSuffix.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <div className="input">
                <input
                  id="customer.phoneNumber"
                  type="text"
                  autoComplete="tel"
                  className="w-full as-text"
                  placeholder={t("PhoneNumber")}
                  {...register("customer.phoneNumber")}
                />
                {formState.errors?.customer?.phoneNumber?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.phoneNumber.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <div className="input">
                <input
                  id="customer.emailAddress"
                  type="text"
                  autoComplete="email"
                  className="w-full as-text"
                  placeholder={t("EmailAddress")}
                  {...register("customer.emailAddress")}
                />
                {formState.errors?.customer?.emailAddress?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer?.emailAddress.message)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </fieldset>
        {showCompanyFieldset && (
          <fieldset className="form-section">
            <legend>{t("CompanyInformation")}</legend>

            {!isAuthenticated && (
              <div className="form-field">
                <div className="input">
                  <label htmlFor="isCompany" className="checkbox">
                    <input
                      type="checkbox"
                      {...register("customer.isCompany")}
                      id="isCompany"
                      onChange={() => {
                        setValue("customer.isCompany", !isCompany);
                        setValue("shippingMethodId", "");
                        setValue("paymentMethodId", "");
                      }}
                    />
                    <span className="text">{t("OrderAsCompany")}</span>
                  </label>
                </div>
              </div>
            )}

            <div
              className={classnames("form-fields", {
                hidden: !isCompany,
              })}
            >
              <div className="form-field">
                <div className="input">
                  <input
                    type="text"
                    className="w-full as-text"
                    placeholder={t("CompanyName")}
                    id="customer.customerName"
                    {...register("customer.customerName")}
                  />
                  {formState.errors?.customer?.customerName?.message && (
                    <span className="field-validation-error">
                      {t(formState.errors.customer.customerName.message)}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <div className="input">
                  <input
                    type="text"
                    className="w-full as-text"
                    placeholder={t("CompanyRegistrationNumber")}
                    id="customer.registrationNumber"
                    maxLength={18}
                    {...register("customer.registrationNumber")}
                  />
                  {formState.errors?.customer?.registrationNumber?.message && (
                    <span className="field-validation-error">
                      {t(formState.errors.customer.registrationNumber.message)}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <div className="input">
                  <input
                    type="text"
                    className="w-full as-text"
                    placeholder={t("CompanyTaxNumber")}
                    id="customer.taxNumber"
                    {...register("customer.taxNumber")}
                  />
                  {formState.errors?.customer?.taxNumber?.message && (
                    <span className="field-validation-error">
                      {t(formState.errors.customer.taxNumber.message)}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <div className="input">
                  <input
                    type="text"
                    className="w-full as-text"
                    placeholder={t("CompanyVatNumber")}
                    id="customer.vatNumber"
                    {...register("customer.vatNumber")}
                  />
                  {formState.errors?.customer?.vatNumber?.message && (
                    <span className="field-validation-error">
                      {t(formState.errors.customer.vatNumber.message)}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <div className="input">
                  <label htmlFor="notVatRegistered" className="checkbox peer/label">
                    <input
                      type="checkbox"
                      {...register("customer.notVatRegistered", {
                        disabled: !allowNotVatRegistered,
                      })}
                      id="notVatRegistered"
                      className="peer/input"
                      // onChange={() => {
                      //   setValue('paymentMethodId', '');
                      // }}
                    />
                    <span className="text peer-disabled/input:text-gray-400">
                      {t("NotVatRegistered")}
                    </span>
                  </label>
                  <span className="text-sm text-red-500 font-medium peer-aria-disabled/label:text-red-300">
                    {t("NotVatRegisteredText")}
                  </span>
                </div>
              </div>
            </div>
          </fieldset>
        )}

        <fieldset className="form-section">
          <legend>{t("BillingAddress")}</legend>

          <div className="form-fields">
            <div className="form-field">
              <div className="input">
                <input
                  id="customer.billingStreet"
                  type="text"
                  autoComplete="street-address"
                  placeholder={t("Street")}
                  className="w-full as-text"
                  {...register("customer.billingStreet")}
                />
                {formState.errors?.customer?.billingStreet?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.billingStreet.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <div className="input">
                <input
                  id="customer.billingTown"
                  type="text"
                  autoComplete="address-level2"
                  className="w-full as-text"
                  placeholder={t("Town")}
                  {...register("customer.billingTown")}
                />
                {formState.errors?.customer?.billingTown?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.billingTown.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <div className="input">
                <input
                  id="customer.billingPostCode"
                  type="text"
                  autoComplete="postal-code"
                  className="w-full as-text"
                  minLength={2}
                  maxLength={16}
                  placeholder={t("PostCode")}
                  {...register("customer.billingPostCode")}
                />
                {formState.errors?.customer?.billingPostCode?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.billingPostCode.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <div className="input">
                <select
                  id="customer.billingCountryCode"
                  autoComplete="country"
                  className="w-full"
                  // placeholder={t('Country')}
                  {...register("customer.billingCountryCode")}
                  onChange={(event) => {
                    const newCountryCode = event.target.value.toLowerCase();
                    if (newCountryCode === defaultCountry) {
                      // setValue('customer.notVatRegistered', false);
                    }
                    setValue("customer.billingCountryCode", newCountryCode);
                  }}
                >
                  <option value="">{t("Select")}</option>
                  {Object.keys(countries).map((code) => (
                    <option
                      key={code}
                      value={code}
                      className={classnames(
                        notVatRegistered && code === defaultCountry && "hidden",
                      )}
                    >
                      {countries[code]}
                    </option>
                  ))}
                </select>
                {formState.errors?.customer?.billingCountryCode?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.billingCountryCode.message)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </fieldset>

        <ShippingAddressFieldset addresses={shippingAddresses} countries={countries} />
      </div>
    </div>
  );
}

